import { FieldViewModel, getDistinctValues, handleApiError, INMaxCropping, IOmniaShell, isNumber, ISoilAnalysis, isUndefinedOrNull, selectMany } from "../../omnia"
import { getNMaxAdjustments } from "../../services/nmax"
import { NutrientFilter } from "../../services/nutrientFilter"
import { ICroppingReqUpdateModel, IFieldsService, INutrient, INutritionService, NutrientType, SoilUnit } from "../../types"
import { getExclusiveAccess, getNutrientKeys } from "../../utils"
import { IBatchEditParameters, IBatchEditResults } from "./batch/types"
import { CroppingAttrs } from "./cropping-attrs"
import { NutrientViewModel } from "./nutrient-vm"
import { ReqZoneVM } from "./req-zone-vm"

export abstract class EditCtrl {

    public readOnly = this.svc.readOnly

    private _releaseLock: Function

    form: ng.IFormController
    showValidationErrors = true

    public showAnalysisIndex: boolean

    protected _soilAnalysis: ISoilAnalysis[]

    public cropping: FieldViewModel
    public zones: ReqZoneVM[]

    protected nutrientsFilter: NutrientFilter
    public nutrients: INutrient[]

    protected _selectedZone: ReqZoneVM

    protected _changes: { croppingId: number, zones: ReqZoneVM[] }[] = []

    private _updatedCropping: number[]

    public nmaxAdjustments = getNMaxAdjustments()
    public nmaxCropping: INMaxCropping

    public showCalculationDetails: boolean

    static $inject = [
        "$scope",
        "OmniaNutritionService",
        "OmniaFieldsService",
        "OmniaShell"
    ]

    constructor(
        protected $scope: ng.IScope,
        protected svc: INutritionService,
        protected fieldSvc: IFieldsService,
        protected shell: IOmniaShell) {

        this.showAnalysisIndex = (svc.getSoilUnit() == SoilUnit.Index)

    }

    protected abstract dispose()
    protected abstract close()

    $onDestroy() {

        const rl = this._releaseLock
        this._releaseLock = undefined
        rl && rl()

        this.dispose()

    }

    protected setup(): ng.IPromise<any> {

        this._changes = []

        return getExclusiveAccess(this.svc, this.shell, true).then(release => {

            this._releaseLock = release
            this.readOnly = this.readOnly || (!release)

            this.nutrientsFilter = new NutrientFilter(this.shell.getSession(), this.svc.settings)
            this.nutrients = this.nutrientsFilter.nutrients

            return this.svc.getSoilAnalysisUsedForRequirements().then(sa => {
                this._soilAnalysis = sa
            })

        })

    }

    protected cancelChanges() {
        this._changes = []
        this.setupCurrentCropping(this.cropping)
    }

    protected setupCurrentCropping(cropping: FieldViewModel) {

        let zones = this.getCroppingZones(cropping)

        this.cropping = cropping
        this.zones = zones

        this.readOnly = this.svc.readOnly || (!cropping?.authorized)

        this.setupNMax()
        this.selectZone(zones[0])

    }

    private setupNMax() {

        this.nmaxCropping = null

        let groups = this.svc.getNMaxCropGroups()

        if (groups && this.cropping) {

            let cropping = selectMany(groups.map(g => g.Cropping))
            this.nmaxCropping = cropping.find(c => c.CroppingId == this.cropping.croppingId)
        }


    }

    protected getCroppingZones(cropping: FieldViewModel): ReqZoneVM[] {

        let target = this._changes.find(c => c.croppingId == cropping.croppingId)

        if (!target) {

            let req = this.svc.getRequirements().filter(r => r.CroppingId == cropping.croppingId).filter(r => !r.PlanId)

            let context = {
                region: this.svc.holding.Region,
                soilAnalysis: this._soilAnalysis,
                watcher: (zone, nutrient) => this.updateOnZoneChanged(zone, nutrient)
            }

            let zones = ReqZoneVM.createZones(context, cropping, req)

            target = {
                croppingId: cropping.croppingId,
                zones: zones,
            }

            this._changes.push(target)

        }

        return target.zones

    }

    get selectedZone(): ReqZoneVM {
        return this._selectedZone
    }

    set selectedZone(value: ReqZoneVM) {
        this.selectZone(value)
    }

    public selectZone(zone: ReqZoneVM) {
        this._selectedZone = zone
    }

    public isZoneSelected(zone: ReqZoneVM): boolean {
        return (this.selectedZone == zone)
    }

    public getVisibleZones(): ReqZoneVM[] {
        return this.zones.filter(z => !z.hidden)
    }

    public canEditComments(): boolean {
        return this.selectedZone && this.selectedZone.wholeField
    }

    public getUpdateModels(): ICroppingReqUpdateModel[] {

        let models = this._changes.map(c => {

            let updatedZones = c.zones.filter(z => z.hasChanged())

            let wholeField = c.zones.find(f => f.wholeField)
            let croppingUpdates: { [key: string]: any }

            croppingUpdates = wholeField.getCalculationParametersChanges()

            if (wholeField.hasSingleWholeFieldZone) {
                updatedZones = [wholeField]
            }

            let model: ICroppingReqUpdateModel = {
                CroppingId: c.croppingId,
                CroppingUpdates: croppingUpdates,
                SyncSingleWholeFieldZone: wholeField.hasSingleWholeFieldZone,
                Zones: updatedZones.map(z => z.getUpdateModel())
            }

            return model

        })

        return models

    }

    public hasChanged(): boolean {
        return this._changes && this._changes.some(c => c.zones.some(z => z.hasChanged()))
    }

    protected editMultipleFields() {

        let args: IBatchEditParameters = {
            nutrients: this.selectedZone.getNutrients(),
            comments: this.selectedZone.comments,
        }

        this.shell.showComponentAsDialog("omnia-nutri-requirements-edit-batch", args, {
            size: "lg"
        }).then(r => this.applyBatchEdit(r))


    }

    protected applyBatchEdit(edit: IBatchEditResults) {

        this.applyBatchEditOnNutrients(edit)
        this.applyWholeFieldBatchEdits(edit)

    }

    protected applyWholeFieldBatchEdits(edit: IBatchEditResults) {

        const comments = !isUndefinedOrNull(edit.comments)
        const pH = !isUndefinedOrNull(edit.targetpH)

        if (!(comments || pH)) {
            return
        }

        edit.croppingIds.forEach(id => {

            let cropping = this.fieldSvc.findCropping(id)

            if (cropping) {

                let zones = this.getCroppingZones(cropping)

                let wholeField = zones.find(z => z.wholeField)

                if (wholeField) {

                    if (comments) {
                        wholeField.comments = edit.comments
                        wholeField.showComments = true
                    }

                    if (pH) {
                        wholeField.croppingAttrs.targetpH = edit.targetpH
                    }

                }

            }

        })
    }

    protected applyBatchEditOnNutrients(edit: IBatchEditResults) {

        if (!edit.nutrients) return

        let nutrients = getNutrientKeys()

        nutrients.forEach(key => {

            let value = edit.nutrients["Required" + key]

            if (isNumber(value)) {

                let nutrientType = NutrientType[key]
                let mask = Math.pow(2, nutrientType - 1)

                edit.croppingIds.forEach(id => {

                    let cropping = this.fieldSvc.findCropping(id)

                    if (cropping) {

                        let zones = this.getCroppingZones(cropping)

                        zones.forEach(z => {

                            if (z.nutrientGroup & mask) {

                                let ntr = z.getNutrient(nutrientType)
                                ntr.setValue(value, true)

                            }
                        })

                    }
                })

            }

        })

    }

    protected updateOnZoneChanged(zone: ReqZoneVM, nutrient?: NutrientViewModel) {
        // NOP
    }

    protected updateOnSaved() {

        let zone = this.selectedZone

        this._changes = []
        this.setupCurrentCropping(this.cropping)

        if (zone) {
            zone = this.zones.find(z => z.zoneNumber == zone.zoneNumber) || zone
            this.selectZone(zone)
        }

    }

    private updateCropping(updates: ICroppingReqUpdateModel[]) {

        updates.filter(m => !!m.CroppingUpdates).forEach(m => {

            let update = m.CroppingUpdates
            let cropping = this.fieldSvc.findCropping(m.CroppingId).cropping

            CroppingAttrs.croppingAttrNames.forEach(key => {

                let value = update[key]
                if (!isUndefinedOrNull(value)) {
                    cropping[key] = value
                }

            })

        })

    }

    public canCommit(): boolean {
        if (this.readOnly) return false
        if (!this.hasChanged()) return false
        if (!this.form.$valid) return false
        return true
    }

    public commit() {

        if (!this.canCommit()) return

        let models = this.getUpdateModels()

        this.svc.updateRequirements(models)
            .then(() => {

                this._updatedCropping = getDistinctValues(models.map(m => m.CroppingId).concat(this._updatedCropping || []), id => id)

                this.updateCropping(models)

                this.updateOnSaved()

            })
            .catch(r => handleApiError(this.shell, r, { defaultMessage: "An error has occurred while updating the requirements" }))

    }

    cancel() {

        if (this.hasChanged()) {
            this.cancelChanges()
            return
        }

        let ids = this._updatedCropping || []
        this._updatedCropping = null

        if (!ids.length) {
            this.disposeAndClose()
        }
        else {
            this.svc.calculateCroppingPlans(ids, false).then(() => this.disposeAndClose())
        }

    }

    protected disposeAndClose() {
        this.dispose()
        this.close()
    }

}
